import { Lock } from "lucide-react";
import { FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { AddressFields } from "@/components/forms/AddressFields";
import { emailFieldError } from "@/lib/emailValidator";
import { phoneFieldError } from "@/lib/phoneValidator";
import { MaskedSensitiveField } from "./MaskedSensitiveField";
import { CollapsibleSection } from "./CollapsibleSection";
import type {
  AccreditedInvestorBasis,
  AccreditedInvestorStatus,
  AddressDto,
  BankAccountType,
  CounterpartyTaxFilingStatus,
  CreateEntityRequest,
  EntityFormationStatus,
  EntityTypeCode,
} from "@/types/counterparties";
import { EMPTY_ADDRESS } from "@/types/counterparties";
import { US_STATES } from "@/lib/usStates";

export type EntityFormState = CreateEntityRequest;

export const EMPTY_ENTITY_FORM: EntityFormState = {
  legalEntityName: "",
  dba: "",
  entityType: "LLC",
  stateOfFormation: "",
  dateOfFormation: null,
  ein: null,
  formationStatus: null,
  primaryEmail: "",
  primaryPhone: "",
  address: EMPTY_ADDRESS,
  mailingAddress: null,
  accreditedInvestorStatus: null,
  accreditedInvestorBasis: null,
  taxFilingStatus: null,
  originalSaleDate: null,
  originalSaleBasisInfo: "",
  bankACHName: "",
  bankRoutingNumber: null,
  bankAccountNumber: null,
  bankAccountType: null,
  authorizedSignersJson: null,
  tags: [],
};

const ENTITY_TYPES: EntityTypeCode[] = ["LLC", "SCorp", "CCorp", "LP", "LLP", "Trust", "SoleProp", "Other"];
const FORMATION: EntityFormationStatus[] = ["PendingFormation", "Formed", "InGoodStanding", "Dissolved", "Other"];
const ACCRED_STATUS: AccreditedInvestorStatus[] = ["Verified", "SelfDeclared", "NotApplicable", "Unknown"];
const ACCRED_BASIS: AccreditedInvestorBasis[] = ["IncomeTest", "NetWorthTest", "EntityTest", "Professional", "Other"];
const TAX_STATUS: CounterpartyTaxFilingStatus[] = ["LLCDisregarded", "LLCPartnership", "SCorp", "CCorp", "Other"];
const BANK_TYPES: BankAccountType[] = ["Checking", "Savings", "Other"];

interface EntityFormProps {
  value: EntityFormState;
  onChange: (next: EntityFormState) => void;
  einMasked?: string | null;
  bankRoutingMasked?: string | null;
  bankAccountMasked?: string | null;
  editingEin?: boolean;
  onEditEin?: () => void;
  editingBank?: boolean;
  onEditBank?: () => void;
}

export function EntityForm({
  value,
  onChange,
  einMasked,
  bankRoutingMasked,
  bankAccountMasked,
  editingEin,
  onEditEin,
  editingBank,
  onEditBank,
}: EntityFormProps) {
  const set = <K extends keyof EntityFormState>(k: K, v: EntityFormState[K]) =>
    onChange({ ...value, [k]: v });
  const setAddress = (a: AddressDto) => set("address", a);
  const setMailing = (a: AddressDto | null) => set("mailingAddress", a);
  const hasMailing = value.mailingAddress != null;

  // Inline format validation; surfaces only after the user has typed something.
  const primaryEmailError = value.primaryEmail
    ? emailFieldError(value.primaryEmail, { required: true })
    : undefined;
  const primaryPhoneError = value.primaryPhone
    ? phoneFieldError(value.primaryPhone, { required: true })
    : undefined;

  return (
    <div className="flex flex-col gap-space-4">
      <Section title="Identity">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3">
          <FormField label="Legal entity name" required className="md:col-span-2">
            <Input
              value={value.legalEntityName}
              onChange={(e) => set("legalEntityName", e.target.value)}
            />
          </FormField>
          <FormField label="DBA" className="md:col-span-2">
            <Input
              value={value.dba ?? ""}
              onChange={(e) => set("dba", e.target.value || null)}
            />
          </FormField>
          <FormField label="Entity type" required>
            <Select
              value={value.entityType}
              onChange={(e) => set("entityType", e.target.value as EntityTypeCode)}
            >
              {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </FormField>
          <FormField label="State of formation">
            <Select
              value={value.stateOfFormation ?? ""}
              onChange={(e) => set("stateOfFormation", e.target.value || null)}
            >
              <option value="">—</option>
              {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
            </Select>
          </FormField>
          <FormField label="Date of formation">
            <Input
              type="date"
              value={value.dateOfFormation ?? ""}
              onChange={(e) => set("dateOfFormation", e.target.value || null)}
            />
          </FormField>
          <FormField label="EIN" helperText="Stored encrypted; masked on read.">
            <MaskedSensitiveField
              label="EIN"
              masked={einMasked}
              editing={editingEin || !einMasked}
              editingValue={value.ein ?? ""}
              onChange={(v) => set("ein", v || null)}
              onRequestEdit={onEditEin}
              placeholder="12-3456789"
            />
          </FormField>
          <FormField label="Formation status">
            <Select
              value={value.formationStatus ?? ""}
              onChange={(e) => set("formationStatus", (e.target.value || null) as EntityFormationStatus | null)}
            >
              <option value="">—</option>
              {FORMATION.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </FormField>
        </div>
      </Section>

      <Section title="Contact">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
          <FormField label="Primary email" required error={primaryEmailError}>
            <Input
              type="email"
              value={value.primaryEmail}
              onChange={(e) => set("primaryEmail", e.target.value)}
              invalid={!!primaryEmailError}
            />
          </FormField>
          <FormField label="Primary phone" required error={primaryPhoneError}>
            <Input
              type="tel"
              value={value.primaryPhone}
              onChange={(e) => set("primaryPhone", e.target.value)}
              invalid={!!primaryPhoneError}
            />
          </FormField>
        </div>
      </Section>

      <Section title="Address">
        <AddressFields value={value.address} onChange={setAddress} required idPrefix="entity-addr" />
        <label className="flex items-center gap-space-2 text-caption mt-space-3">
          <input
            type="checkbox"
            className="h-4 w-4 accent-gold"
            checked={hasMailing}
            onChange={(e) => setMailing(e.target.checked ? { ...value.address } : null)}
          />
          Mailing address differs from physical address
        </label>
        {hasMailing && (
          <div className="mt-space-3">
            <AddressFields
              value={value.mailingAddress ?? EMPTY_ADDRESS}
              onChange={(a) => setMailing(a)}
              required
              idPrefix="entity-mail"
            />
          </div>
        )}
      </Section>

      <CollapsibleSection title="Investor / tax classification">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3">
          <FormField label="Accredited investor status">
            <Select
              value={value.accreditedInvestorStatus ?? ""}
              onChange={(e) => set("accreditedInvestorStatus", (e.target.value || null) as AccreditedInvestorStatus | null)}
            >
              <option value="">—</option>
              {ACCRED_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </FormField>
          <FormField label="Accredited basis">
            <Select
              value={value.accreditedInvestorBasis ?? ""}
              onChange={(e) => set("accreditedInvestorBasis", (e.target.value || null) as AccreditedInvestorBasis | null)}
            >
              <option value="">—</option>
              {ACCRED_BASIS.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </FormField>
          <FormField label="Tax filing status">
            <Select
              value={value.taxFilingStatus ?? ""}
              onChange={(e) => set("taxFilingStatus", (e.target.value || null) as CounterpartyTaxFilingStatus | null)}
            >
              <option value="">—</option>
              {TAX_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </FormField>
          <FormField label="Original sale date">
            <Input
              type="date"
              value={value.originalSaleDate ?? ""}
              onChange={(e) => set("originalSaleDate", e.target.value || null)}
            />
          </FormField>
          <FormField label="Original sale basis info" className="md:col-span-4">
            <Input
              value={value.originalSaleBasisInfo ?? ""}
              onChange={(e) => set("originalSaleBasisInfo", e.target.value || null)}
            />
          </FormField>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Banking"
        warning={
          <span className="inline-flex items-center gap-space-1">
            <Lock size={12} strokeWidth={1.5} aria-hidden /> Encrypted at rest. Routing & account numbers are masked on read.
          </span>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3">
          <FormField label="ACH name on account" className="md:col-span-2">
            <Input
              value={value.bankACHName ?? ""}
              onChange={(e) => set("bankACHName", e.target.value || null)}
            />
          </FormField>
          <FormField label="Routing #">
            <MaskedSensitiveField
              label="Routing number"
              masked={bankRoutingMasked}
              editing={editingBank || !bankRoutingMasked}
              editingValue={value.bankRoutingNumber ?? ""}
              onChange={(v) => set("bankRoutingNumber", v || null)}
              onRequestEdit={onEditBank}
            />
          </FormField>
          <FormField label="Account #">
            <MaskedSensitiveField
              label="Account number"
              masked={bankAccountMasked}
              editing={editingBank || !bankAccountMasked}
              editingValue={value.bankAccountNumber ?? ""}
              onChange={(v) => set("bankAccountNumber", v || null)}
              onRequestEdit={onEditBank}
            />
          </FormField>
          <FormField label="Account type">
            <Select
              value={value.bankAccountType ?? ""}
              onChange={(e) => set("bankAccountType", (e.target.value || null) as BankAccountType | null)}
            >
              <option value="">—</option>
              {BANK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </FormField>
        </div>
      </CollapsibleSection>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mb-space-2 mt-0">
        {title}
      </h3>
      <div className="bg-white-pure rounded-lg border border-border-light p-space-4">
        {children}
      </div>
    </section>
  );
}
